<template>
  <div>
    <template v-if="!itemSelection">
      <ServiceOrderWarnings
        v-if="serviceOrder.order_time || start"
        :service-provider="serviceOrder.service_provider"
        :from-time="serviceOrder.order_time ? new Date(serviceOrder.order_time) : start"
        :category-edit-limit="categoryEditLimit"
      />
      <div class="text-sm">
        <div class="flex justify-between mb-2">
          <span
            class="font-medium"
          >
            {{ $t('Services.Details.Room') }}
          </span>
          <span class="text-neutral-500">
            {{ serviceOrder.bookable.name }}
          </span>
        </div>
        <div class="flex justify-between mb-2">
          <span
            class="font-medium"
          >
            {{ $t('Services.Details.Provider') }}
          </span>
          <span class="text-neutral-500">
            {{ serviceOrder.service_provider.name }}
          </span>
        </div>
        <div class="flex justify-between mb-2">
          <span
            class="font-medium"
          >
            {{ $t('Services.Details.OrderTime') }}
          </span>
          <span class="text-neutral-500">
            {{
              serviceOrder.order_time
                ? new Date(serviceOrder.order_time).toLocaleString()
                : start?.toLocaleString()
            }}
          </span>
        </div>
        <div
          v-if="settings.service_provider_has_end_time"
          class="flex justify-between mb-2"
        >
          <span
            class="font-medium"
          >
            {{ $t('Services.Details.EndTime') }}
          </span>
          <span class="text-neutral-500">
            {{
              serviceOrder.end_time
                ? new Date(serviceOrder.end_time).toLocaleString()
                : end?.toLocaleString()
            }}
          </span>
        </div>
        <div class="flex justify-between mb-2">
          <span
            class="font-medium"
          >
            {{ $t('Services.Details.Status') }}
          </span>
          <ServiceOrderStatus
            :status="serviceOrder.status"
          />
        </div>
      </div>
      <div class="divider my-4" />
      <div class="text-base font-medium mb-4">
        {{ $t('Services.Details.SelectedItems') }}
      </div>
      <ServiceOrderItem
        v-for="item in serviceOrderItems"
        :key="item.id"
        :item="item"
        :not-found="loaded && !availableItems.some(i => i.id === item.id)"
        class="mb-3"
        @change="quantityChange(item, $event)"
      />

      <button
        class="rounded-lg flex items-center justify-center font-medium text-sm border border-dashed border-microsoft-blue-default bg-white h-12 w-full hover:bg-microsoft-blue-default/5 hover:border-microsoft-blue-dark transition mb-4"
        @click="itemSelection = true"
      >
        {{ $t('Services.Details.AddItems') }}
      </button>
      <div
        v-if="settings.service_provider_cost_centers_enabled"
        class="mb-2"
      >
        <span class="input-label">
          {{ $t('Services.Details.CostCenter') }}<span
            v-if="!settings.service_provider_default_cost_centers_allowed"
          >*</span>
        </span>
        <multiselect
          v-model="costCenter"
          class="multiselect-is-single"
          :close-on-select="true"
          :allow-empty="false"
          :options="costCenters"
          label="name"
          open-direction="bottom"
          track-by="id"
        />
        <CollapseTransition>
          <div
            v-if="costCenter?.description"
            class="text-xs text-neutral-500 my-1 mx-2 flex items-start gap-1"
          >
            <Info class="w-3 h-3 shrink-0 mt-0.5" />
            {{ costCenter.description }}
          </div>
        </CollapseTransition>
      </div>

      <label class="block mb-2">
        <div class="input-label">
          {{ $t('Services.Details.NumberOfParticipantsLabel') }}<span
            v-if="serviceOrder.service_provider.requires_number_of_participants"
          >*</span>
        </div>
        <input
          v-model="numberOfParticipants"
          :placeholder="$t('Services.Details.NumberOfParticipants')"
          type="number"
          min="1"
          max="9999"
          class="input w-full"
        >
      </label>

      <label>
        <div class="input-label">
          {{ $t('Services.Details.NotesLabel') }}
        </div>
        <textarea
          v-model="notes"
          :placeholder="$t('Services.Details.NotesPlaceholder')"
          class="input w-full"
          rows="3"
        />
      </label>

      <div class="divider my-4" />
      <div class="text-sm mt-4 mb-4">
        <div class="flex justify-between mb-2">
          <span
            class="font-medium"
          >
            {{ $t('Services.Details.NumberOfItems') }}
          </span>
          <span class="text-neutral-500">
            {{ $tc('Services.Items', totalItems) }}
          </span>
        </div>
        <div class="flex justify-between mb-2">
          <span
            class="font-medium"
          >
            {{ $t('Services.Details.TotalPrice') }}
          </span>
          <span class="text-neutral-500">
            {{ totalPrice }} €
          </span>
        </div>
      </div>
      <AsyncButton
        class="btn btn-primary mb-2 w-full"
        :disabled="!isValid"
        @click="createOrder"
      >
        {{ $t('Services.Details.Confirm') }}
      </AsyncButton>
      <template v-if="serviceOrder.id">
        <AsyncButton
          v-if="serviceOrder.is_editable"
          class="btn btn-danger mb-2 w-full"
          @click="deleteOrder"
        >
          {{ $t('Services.Details.Delete') }}
        </AsyncButton>
        <AsyncButton
          v-else
          class="btn btn-danger mb-2 w-full"
          @click="revokeOrder"
        >
          {{ $t('Services.Details.Revoke') }}
        </AsyncButton>
      </template>
      <button
        class="btn btn-secondary mb-2 w-full"
        @click="$emit('cancel')"
      >
        {{ $t('Services.Details.Cancel') }}
      </button>
    </template>
    <div
      v-else
      class="h-screen w-full fixed top-0 left-0 p-4 flex flex-col pb-4 bg-app-gradient"
    >
      <BackHeader
        class="mb-4"
        :back-text="$t('Services.Details.Back')"
        @click="itemSelection = false"
      />
      <div class="text-base font-medium mb-3">
        {{ $t('Services.Details.SelectItems') }}
      </div>
      <input
        v-model="itemSearch"
        type="search"
        class="input w-full mb-4 shrink-0"
        :placeholder="$t('Services.Details.FilterItems')"
      >
      <div class="overflow-y-auto mb-4 -mx-1 px-1 -mt-1 pt-1">
        <ServiceOrderItem
          v-for="item in combinedItems"
          :key="item.id"
          :item="item"
          class="mb-3"
          @change="quantityChange(item, $event)"
        />
      </div>
      <div class="divider mb-3" />
      <button
        class="btn btn-primary w-full"
        @click="itemSelection = false"
      >
        {{ $t('Services.Details.FinishSelect') }}
      </button>
    </div>
  </div>
</template>

<script>
import outlook from '@/outlook'
import ServiceOrderItem from './ServiceOrderItem.vue'
import api from '@/lib/api'
import cloneDeep from 'lodash/cloneDeep'
import { useNotify } from '@/composables/notify'
import ServiceOrderStatus from './ServiceOrderStatus.vue'
import { useConfirmDialog } from '@/composables/confirmDialog'
import Multiselect from 'vue-multiselect'
import AsyncButton from './AsyncButton.vue'
import ServiceOrderWarnings from './ServiceOrderWarnings.vue'
import BackHeader from './BackHeader.vue'
import { useLoadingBar } from '@/composables/loadingBar'
import CollapseTransition from './Transitions/CollapseTransition.vue'
import { Info } from 'lucide-vue'

export default {
  components: {
    ServiceOrderItem,
    ServiceOrderStatus,
    ServiceOrderWarnings,
    Multiselect,
    AsyncButton,
    BackHeader,
    CollapseTransition,
    Info,
  },
  props: {
    serviceOrder: { type: Object, required: true },
    flexopusEvent: { type: Object, required: true },
    settings: { type: Object, required: true },
    costCenters: { type: Array, required: true },
  },
  setup () {
    return {
      ...outlook.useAppointmentSchedule(),
      ...useNotify(),
      ...useConfirmDialog(),
      ...useLoadingBar(),
    }
  },
  data: () => ({
    serviceOrderItems: [],
    itemSelection: false,
    availableItems: [],
    notes: '',
    costCenter: null,
    numberOfParticipants: null,
    loaded: false,
    itemSearch: '',
  }),
  computed: {
    totalItems () {
      return this.serviceOrderItems.reduce((acc, cur) => acc + cur.quantity, 0)
    },
    totalPrice () {
      return this.serviceOrderItems.reduce((acc, cur) => acc + cur.price * cur.quantity, 0)
    },
    combinedItems () {
      let re = null
      try {
        re = new RegExp(this.itemSearch, 'i')
      } catch {}
      return this.availableItems.map(item => ({
        ...item,
        ...this.serviceOrderItems.find(i => i.id === item.id),
      })).filter(item => re && (re.test(item.name) || re.test(item.item_code)))
    },
    isEditing () {
      return !!this.serviceOrder.id
    },
    isValid () {
      return this.serviceOrderItems.length !== 0 &&
        (
          !this.serviceOrder.service_provider.requires_number_of_participants ||
          this.numberOfParticipants
        ) &&
        (
          this.settings.service_provider_default_cost_centers_allowed ||
          this.costCenter?.id
        )
    },
    categoryEditLimit () {
      const items = this.serviceOrderItems.filter((i) => typeof i.category_limit_in_hours === 'number')

      const maxItem = items.reduce((max, item) => {
        return item.category_limit_in_hours > max.category_limit_in_hours ? item : max
      }, { category_limit_in_hours: 0, category: '' })

      return {
        limit: maxItem.category_limit_in_hours,
        name: maxItem.category,
      }
    },
  },
  async mounted () {
    if (this.isEditing) {
      this.serviceOrderItems = cloneDeep(this.serviceOrder.items)
      this.notes = this.serviceOrder.notes
      this.numberOfParticipants = this.serviceOrder.number_of_participants
    }
    this.availableItems = await this.withLoadingBar(async () => await api.getServiceProviderItems(this.serviceOrder.service_provider.id, this.serviceOrder.bookable.id))
    this.costCenter = (this.serviceOrder.cost_center?.id || this.settings.service_provider_default_cost_centers_allowed)
      ? this.serviceOrder.cost_center
      : null
    this.loaded = true
  },
  methods: {
    quantityChange (item, quantity) {
      const itemIndex = this.serviceOrderItems.findIndex(i => i.id === item.id)
      if (itemIndex === -1) {
        this.serviceOrderItems.push({
          ...cloneDeep(item),
          quantity,
        })
      } else {
        this.serviceOrderItems[itemIndex].quantity = quantity
        if (this.serviceOrderItems[itemIndex].quantity < 1) {
          this.serviceOrderItems.splice(itemIndex, 1)
        }
      }
    },
    async createOrder () {
      try {
        if (this.isEditing) {
          await api.updateServiceOrder(this.serviceOrder.id, {
            notes: this.notes,
            number_of_participants: this.numberOfParticipants,
            selected_service_items: this.serviceOrderItems.map(item => ({
              service_provider_item_id: item.id,
              quantity: item.quantity,
            })),
            cost_center_id: this.costCenter?.id || null,
          })
          this.success(this.$t('Success.OrderUpdated'))
        } else {
          await api.createServiceOrder({
            title: 'Order from Flexopus MSO plugin',
            event_id: this.flexopusEvent.id,
            notes: this.notes,
            number_of_participants: this.numberOfParticipants,
            service_provider_id: this.serviceOrder.service_provider.id,
            bookable_id: this.serviceOrder.bookable.id,
            order_time: this.start.toISOString(),
            end_time: this.end.toISOString(),
            selected_service_items: this.serviceOrderItems.map(item => ({
              service_provider_item_id: item.id,
              quantity: item.quantity,
            })),
            cost_center_id: this.costCenter?.id || null,
          })
          this.success(this.$t('Success.OrderCreated'))
        }
        this.$emit('submit')
      } catch {}
    },
    async deleteOrder () {
      try {
        if (!await this.confirm({
          danger: true,
        })) {
          return
        }
        await api.deleteServiceOrder(this.serviceOrder.id)
        this.success(this.$t('Success.OrderDeleted'))
        this.$emit('submit')
      } catch {}
    },
    async revokeOrder () {
      try {
        if (!await this.confirm({
          danger: true,
        })) {
          return
        }
        await api.revokeServiceOrder(this.serviceOrder.id)
        this.success(this.$t('Success.OrderRevoked'))
        this.$emit('submit')
      } catch {}
    },
  },
}
</script>
